import { useState, useEffect } from 'react';
import { FileText, RefreshCw, Filter, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../lib/api';

export const Loguri = () => {
    const [logs, setLogs] = useState([]);
    const [filter, setFilter] = useState('');
    const [lines, setLines] = useState(100);
    const [loading, setLoading] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(false);

    const loadLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ lines: lines.toString() });
            if (filter) params.append('filter', filter);
            
            const res = await api.get(`/logs/recent?${params}`);
            setLogs(res.data.logs || []);
        } catch (err) {
            console.error('Error loading logs:', err);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadLogs();
    }, []);

    useEffect(() => {
        let interval;
        if (autoRefresh) {
            interval = setInterval(loadLogs, 3000);
        }
        return () => clearInterval(interval);
    }, [autoRefresh, filter, lines]);

    const filteredLogs = logs.filter(log => log && log.trim());

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Loguri Server
                    </h1>
                    <p className="text-zinc-500 text-sm mt-1">Monitorizare în timp real</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        variant={autoRefresh ? "default" : "outline"}
                        className={autoRefresh ? "bg-green-600 hover:bg-green-700" : ""}
                    >
                        <RefreshCw size={16} className={`mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
                        {autoRefresh ? 'Auto ON' : 'Auto OFF'}
                    </Button>
                    <Button onClick={loadLogs} disabled={loading}>
                        <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Reîncarcă
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white border border-zinc-200 rounded-xl p-4 mb-4">
                <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[200px]">
                        <label className="text-xs text-zinc-500 mb-1 block">Filtrează după text</label>
                        <div className="relative">
                            <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                            <Input
                                placeholder="ex: REZERVARE, ERROR, pallet..."
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                    </div>
                    <div className="w-32">
                        <label className="text-xs text-zinc-500 mb-1 block">Nr. linii</label>
                        <Input
                            type="number"
                            value={lines}
                            onChange={(e) => setLines(parseInt(e.target.value) || 50)}
                            min={10}
                            max={500}
                        />
                    </div>
                    <Button variant="outline" onClick={() => { setFilter(''); loadLogs(); }}>
                        <Trash2 size={16} className="mr-2" />
                        Resetează
                    </Button>
                    <Button onClick={loadLogs}>
                        Aplică Filtru
                    </Button>
                </div>
                
                {/* Quick filters */}
                <div className="flex flex-wrap gap-2 mt-3">
                    <span className="text-xs text-zinc-500">Filtre rapide:</span>
                    {['REZERVARE', 'ERROR', 'pallet', 'invoice', 'order'].map(f => (
                        <button
                            key={f}
                            onClick={() => { setFilter(f); }}
                            className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                filter === f 
                                    ? 'bg-violet-100 border-violet-300 text-violet-700' 
                                    : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100'
                            }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            {/* Logs Display */}
            <div className="bg-zinc-900 rounded-xl p-4 overflow-hidden">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <FileText size={16} className="text-green-400" />
                        <span className="text-green-400 text-sm font-mono">backend.err.log</span>
                    </div>
                    <span className="text-zinc-500 text-xs">
                        {filteredLogs.length} linii {filter && `(filtru: "${filter}")`}
                    </span>
                </div>
                
                <div className="bg-black rounded-lg p-3 max-h-[60vh] overflow-y-auto font-mono text-xs">
                    {filteredLogs.length === 0 ? (
                        <div className="text-zinc-500 text-center py-8">
                            {loading ? 'Se încarcă...' : 'Nu există loguri sau filtrul nu a găsit rezultate'}
                        </div>
                    ) : (
                        filteredLogs.map((line, idx) => {
                            // Color coding based on content
                            let textColor = 'text-zinc-300';
                            if (line.includes('ERROR') || line.includes('error') || line.includes('Exception')) {
                                textColor = 'text-red-400';
                            } else if (line.includes('WARNING') || line.includes('warning')) {
                                textColor = 'text-yellow-400';
                            } else if (line.includes('REZERVARE') || line.includes('SUCCESS') || line.includes('success')) {
                                textColor = 'text-green-400';
                            } else if (line.includes('INFO')) {
                                textColor = 'text-blue-400';
                            }
                            
                            return (
                                <div key={idx} className={`${textColor} py-0.5 hover:bg-zinc-800 px-1 rounded`}>
                                    <span className="text-zinc-600 mr-2 select-none">{idx + 1}</span>
                                    {line}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-4 text-xs text-zinc-500">
                <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-red-400"></span> Erori
                </span>
                <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-yellow-400"></span> Avertismente
                </span>
                <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-green-400"></span> Succes / Rezervări
                </span>
                <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-blue-400"></span> Info
                </span>
            </div>
        </div>
    );
};

export default Loguri;
